import { appStore } from "../store/appStore"
import { userStore } from "../store/userStore"

type Request<T> =
    | { status: "success"; data: T; httpStatus?: number }
    | { status: "error"; error: { message: string }; httpStatus?: number }

export const useRequest = () => {
    const { setError } = appStore()

    const requestData = async <T = any>({
        type,
        path,
        sendData,
        disableError = false,
        disableVersion = false
    }: {
        type: "GET" | "POST" | "PUT" | "DELETE" | "PATCH"
        path: string
        sendData?: Record<string, any>
        disableError?: boolean
        disableVersion?: boolean
    }): Promise<Request<T>> => {
        const token = userStore.getState().user?.token ?? null
        const fcmToken = userStore.getState().deviceToken

        const serverUrl = "/api/"
        const userAgent = typeof navigator !== "undefined" ? navigator.userAgent : `node/${process.version}`

        const headers: HeadersInit = {
            Accept: "application/json",
            "Content-Type": "application/json",
            "User-Agent": userAgent,
            "X-Client": "site",
        }

        if (!disableVersion) headers["version"] = "2"
        if (token) headers["Authorization"] = `Bearer ${token}`
        if (fcmToken) {
            // Бэкенд в `kino` использует `x-fcm-token` для валидации наличия токена,
            // и `X-Firebase-ID-Token` для привязки/отвязки токена к пользователю (в т.ч. на logout).
            headers["X-FCM-Token"] = fcmToken
            headers["X-Firebase-ID-Token"] = fcmToken
        }

        const options: RequestInit = {
            method: type,
            headers
        }

        if (sendData) options.body = JSON.stringify(sendData)

        const res = await fetch(serverUrl + path, options)

        let raw: any = null
        try {
            raw = await res.json()
        } catch {
            raw = null
        }

        if (res.status === 429) {
            const message =
                raw?.error?.message ?? raw?.message ?? "Слишком много запросов. Подождите немного."
            const json: Request<T> = { status: "error", error: { message }, httpStatus: 429 }
            if (!disableError) setError(message)
            return json
        }

        // Часть эндпоинтов (например, активация промокода) при успехе отвечает
        // { success: true, data } вместо { status: "success", data } — нормализуем
        const json: Request<T> =
            raw && raw.status === undefined && typeof raw.success === "boolean"
                ? raw.success
                    ? { status: "success", data: raw.data as T, httpStatus: res.status }
                    : {
                          status: "error",
                          error: { message: raw.error?.message ?? "Ошибка запроса" },
                          httpStatus: res.status,
                      }
                : raw && typeof raw === "object"
                  ? { ...raw, httpStatus: res.status }
                  : {
                        status: "error",
                        error: { message: "Ошибка запроса" },
                        httpStatus: res.status,
                    }

        if (json.status === "error" && !disableError) {
            setError(json.error.message)
        }

        return json
    }
    const request = async <T = any>(
        type: "GET" | "POST" | "PUT" | "DELETE" | "PATCH",
        path: string,
        sendData?: Record<string, any>,
        options?: { disableVersion?: boolean; disableError?: boolean }
    ): Promise<T> => {
        const res = await requestData<T>({
            type,
            path,
            sendData,
            disableVersion: options?.disableVersion,
            disableError: options?.disableError,
        })
        console.log(type, path, ...(sendData ? [sendData] : []), res)
        if (res.status === "error") {
            throw new Error(res.error.message)
        }
        return res.data
    }

    return { requestData, request }
}
